// src/lib/plans.js — Single source of truth for all plan limits & features
// ⭐ Every logged-in user defaults to "free". No manual selection needed.

export const PLAN_IDS = {
  FREE: "free",
  SELLER: "seller",
  PRO: "pro",
};

export const PLANS = {
  free: {
    id: "free",
    name: "Starter",
    tagline: "Perfect to explore the marketplace",
    priceMonthly: 0,
    priceYearly: 0,
    icon: "rocket",
    popular: false,
    isFree: true,
    limits: {
      listings: 5,
      aiImages: 3,
      aiVideoSeconds: 0,
      featuredBoosts: 0,
      chatThreads: 20,
      imageCleanPerMonth: 5,
      analytics: false,
      verifiedBadge: false,
      goldBadge: false,
      prioritySupport: false,
    },
    featuresMonthly: [
      { title: "5 Active Listings",   desc: "Post up to 5 items at once — grow your presence." },
      { title: "3 AI Images / month", desc: "Try NanoBanana image generation." },
      { title: "Live Seller Chat",    desc: "Message buyers and sellers in real time." },
      { title: "Social Feed",         desc: "Post, like, comment and share." },
      { title: "Escrow Checkout",     desc: "Safe payments on every confirmed deal." },
    ],
    featuresYearly: [
      { title: "5 Active Listings",   desc: "Post up to 5 items at once — grow your presence." },
      { title: "36 AI Images / year", desc: "Try NanoBanana image generation." },
      { title: "Live Seller Chat",    desc: "Message buyers and sellers in real time." },
      { title: "Social Feed",         desc: "Post, like, comment and share." },
      { title: "Escrow Checkout",     desc: "Safe payments on every confirmed deal." },
    ],
  },

  seller: {
    id: "seller",
    name: "Seller",
    tagline: "For serious sellers who want more reach",
    priceMonthly: 1700,      // ≈ $6
    priceYearly: 16500,      // ≈ $59  (Save 17%)
    icon: "bolt",
    popular: true,
    isFree: false,
    limits: {
      // ⭐ Use the MAXIMUM the plan allows across cycles (80, since yearly grants 80)
      listings: 80,
      aiImages: 30,
      aiVideoSeconds: 30,
      featuredBoosts: 1,
      chatThreads: Infinity,
      imageCleanPerMonth: 50,
      analytics: false,
      verifiedBadge: true,
      goldBadge: false,
      prioritySupport: false,
    },
    featuresMonthly: [
      { title: "35 Active Listings",        desc: "7× more than the free plan." },
      { title: "30 AI Images / month",      desc: "Clean product photos without effort." },
      { title: "1 Featured Boost / month",  desc: "Top of category for 7 days." },
      { title: "Verified Seller Badge",     desc: "Build trust with every buyer." },
      { title: "Background Remover",        desc: "One-click cut-outs for any item." },
      { title: "Escrow Protection",         desc: "Safe payments on every deal." },
    ],
    featuresYearly: [
      { title: "80 Active Listings",        desc: "16× more than the free plan." },
      { title: "360 AI Images / year",      desc: "Clean product photos without effort." },
      { title: "12 Featured Boosts / year", desc: "Top of category for 7 days." },
      { title: "Verified Seller Badge",     desc: "Build trust with every buyer." },
      { title: "Background Remover",        desc: "One-click cut-outs for any item." },
      { title: "Escrow Protection",         desc: "Safe payments on every deal." },
    ],
  },

  pro: {
    id: "pro",
    name: "Pro Seller",
    tagline: "For power sellers and dealers",
    priceMonthly: 2800,      // ≈ $10
    priceYearly: 24900,      // ≈ $89  (Save 17%)
    icon: "crown",
    popular: false,
    isFree: false,
    limits: {
      // ⭐ Pro yearly = Unlimited → so overall cap is Infinity
      listings: Infinity,
      aiImages: 200,
      aiVideoSeconds: 120,
      featuredBoosts: Infinity,
      chatThreads: Infinity,
      imageCleanPerMonth: Infinity,
      analytics: true,
      verifiedBadge: true,
      goldBadge: true,
      prioritySupport: true,
    },
    featuresMonthly: [
      { title: "78 Active Listings",        desc: "Huge capacity — list your full inventory." },
      { title: "200 AI Images / month",     desc: "Full AI Studio access for your store." },
      { title: "Unlimited Featured Boosts", desc: "Stay at the top of your category year-round." },
      { title: "Listing Analytics",         desc: "See views, clicks, chats and conversions." },
      { title: "Priority Support",          desc: "Chat with a human within minutes." },
      { title: "Gold Business Badge",       desc: "Stand out as a verified dealer." },
    ],
    featuresYearly: [
      { title: "Unlimited Listings",        desc: "No caps — list your whole inventory." },
      { title: "2,400 AI Images / year",    desc: "Full AI Studio access for your store." },
      { title: "Unlimited Featured Boosts", desc: "Stay at the top of your category year-round." },
      { title: "Listing Analytics",         desc: "See views, clicks, chats and conversions." },
      { title: "Priority Support",          desc: "Chat with a human within minutes." },
      { title: "Gold Business Badge",       desc: "Stand out as a verified dealer." },
    ],
  },
};

/* ═══════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════ */

// Get plan object from a raw plan id (falls back to free)
export const getPlan = (planId) => {
  const key = String(planId || "free").toLowerCase().trim();
  return PLANS[key] || PLANS.free;
};

// Read a specific limit
export const getLimit = (planId, key) => {
  const plan = getPlan(planId);
  return plan.limits[key];
};

// Check a boolean feature
export const hasFeature = (planId, key) => {
  const plan = getPlan(planId);
  return Boolean(plan.limits[key]);
};

/* ⭐ Return the correct feature list for a billing cycle
   Usage: getPlanFeatures("seller", "yearly")  → array of { title, desc } */
export const getPlanFeatures = (planId, billingCycle = "monthly") => {
  const plan = getPlan(planId);
  return billingCycle === "yearly" ? plan.featuresYearly : plan.featuresMonthly;
};

/* ⭐ Get the correct price for a billing cycle */
export const getPlanPrice = (planId, billingCycle = "monthly") => {
  const plan = getPlan(planId);
  return billingCycle === "yearly" ? plan.priceYearly : plan.priceMonthly;
};

/* ⭐ Normalize ANY input into a valid plan id: 'free' | 'seller' | 'pro'
   Handles:
     - explicit plan ids: "pro", "PRO", " pro "
     - pack names:        "Pro Seller", "Advanced", "Premium", "Elite", "Gold"
     - pack-like names:   "100 Credits", "500 Credits", "Advanced Plan"
     - numeric credits:   { credits: 500 } or { pack_credits: 500 }
     - price in PKR:      { price: 1500 } or { final_amount_pkr: 1500 }
   Always returns one of: "free" | "seller" | "pro"
   Never returns null / undefined.
*/
export const normalizePlanId = (input) => {
  let rawString = "";
  let credits = 0;
  let price = 0;

  if (typeof input === "string") {
    rawString = input;
  } else if (input && typeof input === "object") {
    rawString = input.plan || input.planId || input.pack_name || input.name || "";
    credits   = Number(input.pack_credits ?? input.credits ?? 0);
    price     = Number(
      input.final_amount_pkr ??
      input.amount_pkr ??
      input.pricePKR ??
      input.priceMonthly ??
      input.price ??
      0
    );
  }

  const s = String(rawString).toLowerCase().trim();

  if (s === "pro" || s === "seller" || s === "free") return s;

  if (/(^|[^a-z])(pro|advanced|advance|premium|elite|gold|deluxe|vip|business)([^a-z]|$)/i.test(s)) {
    return "pro";
  }
  if (/(^|[^a-z])(seller|standard|plus|basic|starter|silver)([^a-z]|$)/i.test(s)) {
    return "seller";
  }
  if (/(^|[^a-z])(free|trial)([^a-z]|$)/i.test(s)) {
    return "free";
  }

  if (credits >= 500) return "pro";
  if (credits >= 1)   return "seller";

  if (price >= 1500) return "pro";
  if (price >= 1)    return "seller";

  return "free";
};

/* ⭐ Same as normalizePlanId but returns the full plan object */
export const normalizePlan = (input) => {
  return getPlan(normalizePlanId(input));
};